import { PRIORITY } from '../requestQueue';
// apps/provider/src/services/api/billing.js
// Billing, invoicing, service packages, subscription plans,
// patient packages, superbills, and credit management.
//
// Methods: generateInvoiceFromEncounter, generateClaimFromEncounter,
//          getInvoices, getBillingBalance, getBillingTransactions,
//          getServicePackages, createServicePackage, updateServicePackage, deleteServicePackage,
//          createSuperbill,
//          getPatientPackages, purchasePackage, applyPackageCredits,
//          getSubscriptionPlans, createSubscriptionPlan, updateSubscriptionPlan,
//          getPatientSubscriptions, subscribePatient, cancelPatientSubscription,
//          sendPaymentLink,
//          getAvailableCredits, checkPackagePricing,
//          getClaims, getClaimDetail, updateClaim, submitClaimBatch, pollClaimStatus,
//          getRemittances, getRemittanceDetail, postRemittance, manualMatchERA,
//          pollERAList, fetchERADetails, getUnassignedRemittances, assignRemittanceOrg,
//          getUnattachedRemittances,
//          getOrganizationPayers, createOrganizationPayer, updateOrganizationPayer, deleteOrganizationPayer,
//          getPayerGroups, createPayerGroup, updatePayerGroup, deletePayerGroup, seedPayerGroups,
//          getProviderPayerCredentialing, upsertProviderPayerCredentialing, deleteProviderPayerCredentialing,
//          searchPayers, getFeeSchedule, createFeeScheduleEntry, updateFeeScheduleEntry,
//          deleteFeeScheduleEntry, importFeeSchedule, submitClaimAttachment,
//          feeLookup, importMedicareRVU, importMedicareGPCI, importMedicareLocality,
//          getPatientPaymentMethods, createSetupIntent, setPatientDefaultPaymentMethod,
//          deletePatientPaymentMethod,
//          getEpisodeState, runDerivationAudit, runLedgerBackfill, runChainRepair
//
// Response types: @prism-ehr/shared → Invoice, BillingBalance, BillingTransaction,
//                 ServicePackage, PatientPackage, PackagePurchaseResult,
//                 SubscriptionPlan, PatientSubscription, SubscriptionResult,
//                 Superbill, AvailableCredits,
//                 InsuranceClaimResponse, ERAResponse, ERADetailResponse,
//                 FeeScheduleResponse, PayerSearchResult, ClaimAttachmentResponse,
//                 FeeLookupResponse, OrganizationPayerResponse,
//                 PayerGroupResponse, PayerGroupSeedResponse, ProviderPayerCredentialingResponse

// The month-end billing endpoints sit behind the reporting admission gate
// (Xano functions/reporting_admission) and answer 429 when the instance is
// saturated; those getters retry through retryOnBusy.
import { retryOnBusy } from '../../utils/retryOnBusy';

/**
 * @returns {Object} Billing domain methods (bound to `this` of the composed apiService)
 */
export function createBillingMethods() {
  return {

    // ============ Invoice & Claim Generation ============

    /** Independent, nonblocking Billing Review activity ingestion. */
    recordBillingReviewEvents(body) {
      return this.request('/billing-review-events', { method: 'POST', body: JSON.stringify(body) }, 'recordBillingReviewEvents', { priority: PRIORITY.LOW });
    },

    /** @returns {Promise<import('@prism-ehr/shared/types/api/billing').BillingAutomationSummary>} */
    getBillingAutomationSummary(encounterIds = []) {
      const params = new URLSearchParams();
      if (encounterIds.length) params.set('encounter_ids', encounterIds.slice(0, 25).join(','));
      return this.request(`/billing-automation-summary?${params}`, { method: 'GET' });
    },

    /**
     * Generate an invoice from a completed encounter.
     *
     * Xano: POST /billing/invoices/generate-from-encounter
     * Xano staging file: 387_billing_invoices_generate_from_encounter_POST.xs
     *
     * @param {string} encounterId
     * @param {string} [eraDetailId] - Optional ERA detail ID. When provided, invoice uses
     *   actual amounts from the ERA (total_paid, total_patient_responsibility, etc.) instead
     *   of estimated amounts. Used for backfilling invoices on already-paid claims.
     * @returns {Promise<import('@prism-ehr/shared').Invoice>}
     */
    generateInvoiceFromEncounter(encounterId, eraDetailId) {
      const body = eraDetailId
        ? { encounter_id: encounterId, era_detail_id: eraDetailId }
        : { encounter_id: encounterId };
      return this.request('/billing/invoices/generate-from-encounter', {
        method: 'POST',
        body: JSON.stringify(body)
      });
    },

    /**
     * Generate an insurance claim from a completed encounter.
     *
     * Xano: POST /claim/generate-from-encounter
     * Xano staging file: 964_claim_generate_from_encounter_POST.xs
     *
     * @param {string} encounterId
     * @param {boolean} [overrideSoftWarnings=false] - Override soft billing rule warnings
     * @param {string} [frequencyCode] - Claim frequency: '1'=new, '7'=corrected, '8'=voided
     * @param {string} [originalClaimId] - Original claim ID for corrected/voided resubmissions
     * @param {boolean} [dryRun] - If true, returns preview without persisting
     * @param {string} [billingEntityId] - ID of an alternate billing entity from organization.settings.billing_entities; omit to bill under the org defaults
     * @param {boolean} [skipEncounterLink] - Skip stamping encounter.insurance_claim_id; used by the Veradigm fresh-billing flow, where veradigm-pm-post-charges links the encounter only after charges actually post
     * @param {string} [priorAuthorizationId] - Prior authorization the biller picked in the review modal; server attribution is the fallback when omitted
     * @param {boolean} [replaceUnsent] - originalClaimId is a never-submitted claim being replaced (Edit Codes on an unsent claim); the server refuses if it has gone out since
     * @returns {Promise<{success: boolean, claim?: Object, errors?: string[], violations?: Object[], dry_run?: boolean}>}
     */
    generateClaimFromEncounter(encounterId, overrideSoftWarnings = false, frequencyCode, originalClaimId, dryRun, billingEntityId, skipEncounterLink, priorAuthorizationId, replaceUnsent) {
      const body = {
        encounter_id: encounterId,
        override_soft_warnings: overrideSoftWarnings,
      };
      if (frequencyCode) body.frequency_code = frequencyCode;
      if (originalClaimId) body.original_claim_id = originalClaimId;
      if (dryRun) body.dry_run = true;
      if (billingEntityId) body.billing_entity_id = billingEntityId;
      if (skipEncounterLink) body.skip_encounter_link = true;
      if (priorAuthorizationId) body.prior_authorization_id = priorAuthorizationId;
      if (replaceUnsent) body.replace_unsent = true;
      return this.request('/claim/generate-from-encounter', {
        method: 'POST',
        body: JSON.stringify(body)
      });
    },

    getAgingReport(filters = {}) {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, val]) => {
        if (val !== undefined && val !== null) params.append(key, val);
      });
      return retryOnBusy(() => this.request(`/billing/aging-report?${params.toString()}`, { method: 'GET', timeoutMs: 90000 }), {});
    },

    getRemittance835(eraId) {
      return this.request(`/billing/era-835/${eraId}`, { method: 'GET' });
    },

    getRemittancePdf(eraId) {
      return this.request(`/billing/era-pdf/${eraId}`, { method: 'GET' });
    },

    generateSecondaryClaim(parentClaimId, options = {}) {
      // options: { regenerate: bool (supersede existing live secondary),
      //            force: bool (override crossover block for stalled crossovers),
      //            force_msp: bool (confirmed Medigap; logged),
      //            confirm_primary_remit: bool (hand-keyed remit confirmed as the primary payer's; logged) }
      return this.request('/claim/generate-secondary', {
        method: 'POST',
        body: JSON.stringify({ parent_claim_id: parentClaimId, ...options })
      });
    },

    // ============ Invoice Creation ============

    /**
     * Get prefilled line items for invoice creation from encounters.
     *
     * Xano: GET /billing/prefill-invoice
     *
     * @param {string[]} encounterIds
     * @returns {Promise<import('@prism-ehr/shared').InvoicePrefillResponse>}
     */
    prefillInvoice(encounterIds) {
      const params = new URLSearchParams();
      params.append('encounter_ids', encounterIds.join(','));
      return this.request(`/billing/prefill-invoice?${params.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Create a new invoice with custom line items.
     *
     * Xano: POST /billing/invoices/create
     *
     * @param {Object} invoiceData
     * @param {string} invoiceData.patient_id
     * @param {string[]} [invoiceData.encounter_ids]
     * @param {Object[]} invoiceData.line_items
     * @param {number} [invoiceData.discount_amount]
     * @param {number} [invoiceData.tax_amount]
     * @param {string} [invoiceData.notes]
     * @param {string} [invoiceData.payment_terms]
     * @param {string} [invoiceData.due_date]
     * @param {string} [invoiceData.invoice_type]
     * @returns {Promise<import('@prism-ehr/shared').Invoice>}
     */
    createInvoice(invoiceData) {
      return this.request('/billing/invoices/create', {
        method: 'POST',
        body: JSON.stringify(invoiceData)
      });
    },

    /**
     * Update an existing invoice.
     *
     * Xano: PATCH /invoice/{id}
     * Xano staging file: 841_invoice_invoice_id_PATCH.xs
     *
     * @param {string} id
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared').Invoice>}
     */
    updateInvoice(id, data) {
      return this.request(`/invoice/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    /**
     * Delete an invoice and clear linked encounter/appointment references.
     *
     * Xano: DELETE /invoice/{id}
     * Xano staging file: 837_invoice_invoice_id_DELETE.xs
     *
     * @param {string} id - Invoice ID
     * @returns {Promise<void>}
     */
    deleteInvoice(id) {
      return this.request(`/invoice/${id}`, {
        method: 'DELETE'
      });
    },

    /**
     * Create a consolidated statement from multiple unpaid invoices.
     *
     * Xano: POST /billing/invoices/create-statement
     *
     * @param {import('@prism-ehr/shared').CreateStatementInput} statementData
     * @returns {Promise<import('@prism-ehr/shared').Invoice>}
     */
    createStatement(statementData) {
      return this.request('/billing/invoices/create-statement', {
        method: 'POST',
        body: JSON.stringify(statementData)
      });
    },

    // ============ Invoices ============

    /**
     * Get invoices with optional filters.
     *
     * Xano: GET /billing/invoices
     * Xano staging file: 875_billing_invoices_GET.xs
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.organization_id]
     * @param {string} [filters.patient_id]
     * @param {string} [filters.status] - comma-separated list of statuses
     * @param {string} [filters.start_date]
     * @param {string} [filters.end_date]
     * @param {string} [filters.provider_id]
     * @param {string} [filters.invoice_type]
     * @param {string} [filters.due_date_start]
     * @param {string} [filters.due_date_end]
     * @param {boolean} [filters.exclude_consolidated]
     * @param {string} [filters.query] - patient name search term
     * @param {{sortBy: string, orderBy: string}} [filters.external] - sort override; ignored while searching
     * @param {number} [filters.page]
     * @param {number} [filters.limit]
     * @returns {Promise<{items: import('@prism-ehr/shared').Invoice[], pagination: {page: number, per_page: number, total: number}}>}
     */
    getInvoices(filters = {}) {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, val]) => {
        if (val === undefined || val === null || val === '') return;
        queryParams.append(key, key === 'external' ? JSON.stringify(val) : val);
      });
      const qs = queryParams.toString();
      return this.request(`/billing/invoices${qs ? `?${qs}` : ''}`, { method: 'GET' });
    },

    /**
     * Record a manual (non-Stripe) payment on an invoice.
     *
     * Xano: POST /billing/invoices/record-payment
     *
     * @param {Object} paymentData
     * @param {string} paymentData.invoice_id
     * @param {number} paymentData.amount
     * @param {string} paymentData.method - cash | check | zelle | venmo | other
     * @param {string} [paymentData.reference]
     * @param {string} [paymentData.notes]
     * @param {string} [paymentData.payment_date] - YYYY-MM-DD effective payment date (e.g. check date); defaults to entry time
     * @param {string} [paymentData.adjustment_type] - contractual | write_off | correction; creates an invoice_adjustment for the remainder atomically
     * @param {number} [paymentData.adjustment_amount] - required with adjustment_type; amount + adjustment_amount must not exceed balance_due
     * @param {string} [paymentData.adjustment_reason]
     * @param {string} [paymentData.proof_file_data] - base64 encoded file
     * @param {string} [paymentData.proof_file_name]
     * @returns {Promise<import('@prism-ehr/shared').Invoice>}
     */
    recordManualPayment(paymentData) {
      return this.request('/billing/invoices/record-payment', {
        method: 'POST',
        body: JSON.stringify(paymentData)
      });
    },

    /**
     * Edit a manual payment in place (array position preserved).
     * Send the full current values; the endpoint diffs them against the stored
     * entry. Amount / payment_date changes move invoice totals by the delta and
     * offset + re-append the ledger fact; other fields only rewrite the entry.
     *
     * Xano: POST /billing/invoices/edit-payment
     * Xano staging file: billing_invoices_edit-payment_POST.xs
     *
     * @param {Object} data
     * @param {string} data.invoice_id
     * @param {number} data.payment_index - index into invoice.manual_payments
     * @param {number} [data.amount]
     * @param {string} [data.method] - cash | check | ach | zelle | venmo | other
     * @param {string} [data.reference]
     * @param {string} [data.notes]
     * @param {string} [data.payment_date] - YYYY-MM-DD
     * @param {string} [data.proof_file_data] - base64 encoded replacement proof
     * @param {string} [data.proof_file_name]
     * @returns {Promise<import('@prism-ehr/shared').Invoice>}
     */
    editManualPayment(data) {
      return this.request('/billing/invoices/edit-payment', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Void (soft-delete) a manual payment. The entry stays in
     * invoice.manual_payments with voided_at / voided_by / voided_reason so
     * proof, bank-rec and ledger references keep their index; invoice totals
     * are reversed, a companion remainder adjustment is voided with it, and a
     * negative patient_payment ledger fact is appended.
     *
     * Xano: POST /billing/invoices/void-payment
     * Xano staging file: billing_invoices_void-payment_POST.xs
     *
     * @param {{ invoice_id: string, payment_index: number, reason?: string }} data
     * @returns {Promise<import('@prism-ehr/shared').Invoice>}
     */
    voidManualPayment(data) {
      return this.request('/billing/invoices/void-payment', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    // ============ Billing Balance & Transactions ============

    /**
     * Get current billing balance (Stripe integration).
     *
     * Xano: GET /billing/balance
     * Xano staging file: 849_billing_balance_GET.xs
     *
     * @returns {Promise<import('@prism-ehr/shared').BillingBalance>}
     */
    getBillingBalance() {
      return this.request('/billing/balance', {
        method: 'GET'
      });
    },

    /**
     * Get billing transactions with optional filters.
     *
     * Xano: GET /billing/transactions
     * Xano staging file: 850_billing_transactions_GET.xs
     *
     * @param {Object} [filters={}]
     * @param {number} [filters.page]
     * @param {number} [filters.limit]
     * @returns {Promise<{items: import('@prism-ehr/shared').BillingTransaction[], pagination: {page: number, per_page: number, total: number}}>}
     */
    getBillingTransactions(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.patient_id) queryParams.append('patient_id', filters.patient_id);
      if (filters.page) queryParams.append('page', filters.page);
      if (filters.limit) queryParams.append('limit', filters.limit);

      return this.request(`/billing/transactions?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    // ============ Service Packages ============

    /**
     * Get service packages with optional filters.
     *
     * Xano: GET /service_package
     * Xano staging file: 853_service_package_GET.xs
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.organization_id]
     * @param {boolean} [filters.active]
     * @returns {Promise<import('@prism-ehr/shared').ServicePackage[]>}
     */
    getServicePackages(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.organization_id) queryParams.append('organization_id', filters.organization_id);
      if (filters.active !== undefined) queryParams.append('active', filters.active);

      return this.request(`/service_package?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Create a new service package.
     *
     * Xano: POST /service_package
     * Xano staging file: 854_service_package_POST.xs
     *
     * @param {Object} packageData
     * @returns {Promise<import('@prism-ehr/shared').ServicePackage>}
     */
    createServicePackage(packageData) {
      return this.request('/service_package', {
        method: 'POST',
        body: JSON.stringify(packageData)
      });
    },

    /**
     * Update an existing service package.
     *
     * Xano: PATCH /service_package/{id}
     * Xano staging file: 855_service_package_service_package_id_PATCH.xs
     *
     * @param {string} id - Service package ID
     * @param {Object} updates
     * @returns {Promise<import('@prism-ehr/shared').ServicePackage>}
     */
    updateServicePackage(id, updates) {
      return this.request(`/service_package/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates)
      });
    },

    /**
     * Delete a service package.
     *
     * Xano: DELETE /service_package/{id}
     * Xano staging file: 851_service_package_service_package_id_DELETE.xs
     *
     * @param {string} id - Service package ID
     * @returns {Promise<void>}
     */
    deleteServicePackage(id) {
      return this.request(`/service_package/${id}`, {
        method: 'DELETE'
      });
    },

    // ============ Superbills ============

    /**
     * Create a new superbill.
     *
     * Xano: POST /superbill
     * Xano staging file: 1033_superbill_POST.xs
     *
     * @param {Object} superbillData
     * @returns {Promise<import('@prism-ehr/shared').Superbill>}
     */
    createSuperbill(superbillData) {
      return this.request('/superbill', {
        method: 'POST',
        body: JSON.stringify(superbillData)
      }, 'createSuperbill');
    },

    // ============ Invoice Adjustments ============

    createInvoiceAdjustment(data) {
      return this.request('/invoice-adjustment', {
        method: 'POST',
        body: JSON.stringify(data)
      }, 'createInvoiceAdjustment');
    },

    getInvoiceAdjustments(invoiceId) {
      return this.request(`/invoice-adjustment?invoice_id=${invoiceId}`, {}, 'getInvoiceAdjustments');
    },

    voidInvoiceAdjustment(adjustmentId, reason) {
      return this.request(`/invoice-adjustment/${adjustmentId}`, {
        method: 'PATCH',
        body: JSON.stringify({ voided_reason: reason })
      }, 'voidInvoiceAdjustment');
    },

    // ============ Patient Packages ============

    /**
     * Get patient's purchased packages.
     *
     * Xano: GET /patient_package
     * Xano staging file: 880_patient_package_GET.xs
     *
     * @param {string} patientId
     * @param {boolean} [includeExpired=false]
     * @returns {Promise<import('@prism-ehr/shared').PatientPackage[]>}
     */
    getPatientPackages(patientId, includeExpired = false) {
      const queryParams = new URLSearchParams();
      queryParams.append('patient_id', patientId);
      if (includeExpired) queryParams.append('include_expired', 'true');

      return this.request(`/patient_package?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Purchase a service package for a patient. Creates Stripe payment intent.
     *
     * Xano: POST /patient-packages/purchase
     * Xano staging file: 888_patient_packages_purchase_POST.xs
     *
     * Side effects: Creates Stripe customer (if needed), payment intent,
     * patient_package record, and payment record with status 'pending'.
     *
     * @param {Object} purchaseData
     * @param {string} purchaseData.package_id - Service package ID
     * @param {string} [purchaseData.patient_id] - Patient ID (provider purchasing for patient; omit for patient self-purchase)
     * @param {boolean} [purchaseData.save_payment_method]
     * @param {string} [purchaseData.payment_method_id] - Saved payment method ID (skip Stripe Elements flow)
     * @returns {Promise<import('@prism-ehr/shared').PackagePurchaseResult>}
     */
    purchasePackage(purchaseData) {
      return this.request('/patient-packages/purchase', {
        method: 'POST',
        body: JSON.stringify(purchaseData)
      });
    },

    /**
     * Apply package credits to a service (not currently in use).
     *
     * Xano: POST /patient-packages/apply-credits
     * Xano staging file: Not found in staging
     *
     * @param {Object} creditData
     * @returns {Promise<Object>}
     */
    applyPackageCredits(creditData) {
      return this.request('/patient-packages/apply-credits', {
        method: 'POST',
        body: JSON.stringify(creditData)
      });
    },

    // ============ Subscription Plans ============

    /**
     * Get subscription plans with optional filters.
     *
     * Xano: GET /subscription_plan
     * Xano staging file: 885_subscription_plan_GET.xs
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.organization_id]
     * @param {boolean} [filters.active]
     * @param {string} [filters.plan_type]
     * @returns {Promise<import('@prism-ehr/shared').SubscriptionPlan[]>}
     */
    getSubscriptionPlans(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.organization_id) queryParams.append('organization_id', filters.organization_id);
      if (filters.active !== undefined) queryParams.append('active', filters.active);
      if (filters.plan_type) queryParams.append('plan_type', filters.plan_type);

      return this.request(`/subscription_plan?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Create a new subscription plan.
     *
     * Xano: POST /subscription_plan
     * Xano staging file: 886_subscription_plan_POST.xs
     *
     * @param {Object} planData
     * @returns {Promise<import('@prism-ehr/shared').SubscriptionPlan>}
     */
    createSubscriptionPlan(planData) {
      return this.request('/subscription_plan', {
        method: 'POST',
        body: JSON.stringify(planData)
      });
    },

    /**
     * Update an existing subscription plan.
     *
     * Xano: PATCH /subscription_plan/{id}
     * Xano staging file: 887_subscription_plan_subscription_plan_id_PATCH.xs
     *
     * @param {string} id - Subscription plan ID
     * @param {Object} updates
     * @returns {Promise<import('@prism-ehr/shared').SubscriptionPlan>}
     */
    updateSubscriptionPlan(id, updates) {
      return this.request(`/subscription_plan/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(updates)
      });
    },

    /**
     * Delete a subscription plan.
     *
     * Xano: DELETE /subscription_plan/{id}
     * Xano staging file: 883_subscription_plan_subscription_plan_id_DELETE.xs
     *
     * @param {string} id - Subscription plan ID
     * @returns {Promise<void>}
     */
    deleteSubscriptionPlan(id) {
      return this.request(`/subscription_plan/${id}`, {
        method: 'DELETE'
      });
    },

    // ============ Patient Subscriptions ============

    /**
     * Get patient's active subscriptions (payment plans).
     *
     * Xano: GET /payment_plan
     * Xano staging file: 892_payment_plan_GET.xs
     *
     * @param {string} patientId
     * @param {boolean} [activeOnly=true]
     * @returns {Promise<import('@prism-ehr/shared').PatientSubscription[]>}
     */
    getPatientSubscriptions(patientId, activeOnly = true) {
      const queryParams = new URLSearchParams();
      queryParams.append('patient_id', patientId);
      if (activeOnly) queryParams.append('status', 'active');

      return this.request(`/payment_plan?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Get all subscribers for a specific subscription plan.
     *
     * Xano: GET /payment_plan?subscription_plan_id={planId}
     *
     * @param {string} planId - Subscription plan ID
     * @returns {Promise<Array>}
     */
    getSubscriptionPlanSubscribers(planId) {
      return this.request(`/payment_plan?subscription_plan_id=${planId}`, {
        method: 'GET'
      });
    },

    /**
     * Subscribe a patient to a subscription plan. Creates Stripe subscription.
     *
     * Xano: POST /patient-subscriptions/subscribe
     * Xano staging file: 895_patient_subscriptions_subscribe_POST.xs
     *
     * @param {Object} subscriptionData
     * @param {string} subscriptionData.plan_id - Subscription plan ID
     * @param {string} [subscriptionData.patient_id] - Patient ID (for provider-initiated)
     * @returns {Promise<import('@prism-ehr/shared').SubscriptionResult>}
     */
    subscribePatient(subscriptionData) {
      return this.request('/patient-subscriptions/subscribe', {
        method: 'POST',
        body: JSON.stringify(subscriptionData)
      });
    },

    /**
     * Cancel a patient's subscription.
     *
     * Xano: POST /patient-subscriptions-cancel
     * Xano staging file: patient-subscriptions-cancel_POST.xs
     *
     * @param {string} subscriptionId
     * @param {boolean} [cancelAtPeriodEnd=true] - If true, subscription remains active until period ends
     * @returns {Promise<import('@prism-ehr/shared').CancelSubscriptionResult>}
     */
    /**
     * Sync subscription status from Stripe after payment confirmation.
     *
     * Xano: POST /patient-subscriptions-confirm
     *
     * @param {string} subscriptionId - payment_plan ID
     * @returns {Promise<{status: string, subscription_id: string}>}
     */
    confirmSubscription(subscriptionId) {
      return this.request('/patient-subscriptions-confirm', {
        method: 'POST',
        body: JSON.stringify({ subscription_id: subscriptionId })
      });
    },

    cancelPatientSubscription(subscriptionId, patientId, cancelAtPeriodEnd = true) {
      return this.request('/patient-subscriptions-cancel', {
        method: 'POST',
        body: JSON.stringify({
          subscription_id: subscriptionId,
          patient_id: patientId,
          cancel_at_period_end: cancelAtPeriodEnd
        })
      });
    },

    // ============ Payment Links ============

    /**
     * Send a payment link to a patient via email.
     *
     * Xano: POST /billing/send-payment-link
     * Xano staging file: billing_send_payment_link_POST.xs
     *
     * Side effects: Creates Stripe Payment Link, sends email via SES,
     * updates invoice sent_at, writes audit log entry.
     * Uses invoice.recipient.email if set, otherwise falls back to patient email.
     *
     * @param {string} invoiceId
     * @param {string} patientId
     * @returns {Promise<{success: boolean, payment_link_url: string}>}
     */
    sendPaymentLink(invoiceId, patientId) {
      return this.request('/billing/send-payment-link', {
        method: 'POST',
        body: JSON.stringify({
          invoice_id: invoiceId,
          patient_id: patientId
        })
      });
    },

    /**
     * Email the superbill link for an invoice without a payment link
     * (paid in person / portal / card on file, or a resend).
     *
     * Xano: POST /billing/send-superbill (billing_send-superbill_POST.xs)
     *
     * @param {string} invoiceId
     * @returns {Promise<{success: boolean, message_id: string|null}>}
     */
    sendSuperbill(invoiceId) {
      return this.request('/billing/send-superbill', {
        method: 'POST',
        body: JSON.stringify({ invoice_id: invoiceId })
      });
    },

    /**
     * Charge the patient's card on file for an invoice's balance, confirmed
     * server-side. Card errors resolve with {success:false, declined:true}
     * rather than rejecting, so callers can fall back to a payment link.
     *
     * Xano: POST /billing/charge-saved-payment-method
     *       (billing_charge-saved-payment-method_POST.xs)
     *
     * @param {string} invoiceId
     * @param {string} [paymentMethodId] Stripe pm_ id; defaults to the customer's default card
     * @returns {Promise<{success: boolean, declined?: boolean, code?: string, decline_code?: string, message?: string, payment_intent_id?: string, card_brand?: string, card_last_four?: string, amount?: number, receipt_url?: string}>}
     */
    chargeSavedPaymentMethod(invoiceId, paymentMethodId) {
      return this.request('/billing/charge-saved-payment-method', {
        method: 'POST',
        body: JSON.stringify({ invoice_id: invoiceId, payment_method_id: paymentMethodId || undefined })
      });
    },

    /**
     * Get payment records for an invoice.
     *
     * Xano: GET /billing/payments
     * Xano staging file: billing_payments_GET.xs
     *
     * @param {{ invoice_id: string }} filters
     * @returns {Promise<{ items: import('@prism-ehr/shared').Payment[] }>}
     */
    getPayments(filters) {
      const params = new URLSearchParams(filters).toString();
      return this.request(`/billing/payments?${params}`);
    },

    /**
     * Process a refund on a Stripe payment.
     *
     * Xano: POST /billing/refund-payment
     * Xano staging file: billing_refund-payment_POST.xs
     *
     * @param {{ payment_id: string, amount?: number, reason?: string, notes?: string }} refundData
     * @returns {Promise<import('@prism-ehr/shared').RefundPaymentResponse>}
     */
    refundPayment(refundData) {
      return this.request('/billing/refund-payment', {
        method: 'POST',
        body: JSON.stringify(refundData)
      });
    },

    /**
     * Update the Stripe statement descriptor for the connected account.
     *
     * Xano: PATCH /billing/connect/update-descriptor
     * Xano staging file: billing_connect_update-descriptor_PATCH.xs
     *
     * @param {string} descriptor - Max 22 characters
     * @returns {Promise<import('@prism-ehr/shared').BillingAccount>}
     */
    updateStatementDescriptor(descriptor) {
      return this.request('/billing/connect/update-descriptor', {
        method: 'PATCH',
        body: JSON.stringify({ statement_descriptor: descriptor })
      });
    },

    /**
     * Get the Stripe Connect account status for an organization.
     *
     * Xano: GET /billing/connect/status
     *
     * @param {string} organizationId
     * @returns {Promise<{onboarding_status: string, stripe_account_id: string}>}
     */
    getConnectStatus(organizationId) {
      return this.request(`/billing/connect/status?organization_id=${organizationId}`, {
        method: 'GET'
      });
    },

    /**
     * Create a Stripe Connect account for an organization.
     *
     * Xano: POST /billing/connect/create-account
     *
     * @param {string} organizationId
     * @returns {Promise<{stripe_account_id: string}>}
     */
    createConnectAccount(organizationId) {
      return this.request('/billing/connect/create-account', {
        method: 'POST',
        body: JSON.stringify({ organization_id: organizationId })
      });
    },

    /**
     * Get a Stripe Connect onboarding link for an organization.
     *
     * Xano: POST /billing/connect/onboarding-link
     *
     * @param {string} organizationId
     * @param {string} returnUrl
     * @param {string} refreshUrl
     * @returns {Promise<{url: string}>}
     */
    getConnectOnboardingLink(organizationId, returnUrl, refreshUrl) {
      return this.request('/billing/connect/onboarding-link', {
        method: 'POST',
        body: JSON.stringify({ organization_id: organizationId, return_url: returnUrl, refresh_url: refreshUrl })
      });
    },

    /**
     * Get a Stripe Express dashboard login link.
     *
     * Xano: POST /billing/dashboard-link
     *
     * @param {string} organizationId
     * @returns {Promise<{url: string}>}
     */
    getDashboardLink(organizationId) {
      return this.request('/billing/dashboard-link', {
        method: 'POST',
        body: JSON.stringify({ organization_id: organizationId })
      });
    },

    // ============ Credit & Billing Integration ============

    /**
     * Get available credits for a patient, optionally filtered by appointment type.
     *
     * Xano: GET /billing/available-credits
     * Xano staging file: Not found in staging
     *
     * @param {string} patientId
     * @param {string} [appointmentTypeId]
     * @returns {Promise<import('@prism-ehr/shared').AvailableCredits>}
     */
    getAvailableCredits(patientId, appointmentTypeId = null) {
      const queryParams = new URLSearchParams();
      queryParams.append('patient_id', patientId);
      if (appointmentTypeId) queryParams.append('appointment_type_id', appointmentTypeId);

      return this.request(`/billing/available-credits?${queryParams.toString()}`, {
        method: 'GET'
      });
    },

    /**
     * Check pricing details for a service package.
     *
     * Xano: GET /service-packages/{id}/pricing
     * Xano staging file: Not found in staging (PATCH exists: 857_billing_appointment_type_appointment_type_id_pricing_PATCH.xs)
     *
     * @param {string} packageId - Service package ID
     * @returns {Promise<Object>}
     */
    checkPackagePricing(packageId) {
      return this.request(`/service-packages/${packageId}/pricing`, {
        method: 'GET'
      });
    },

    // ============ Insurance Claims ============

    /**
     * Get insurance claims with optional filters.
     *
     * Xano: GET /billing/claims
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.status] - Comma-separated status list
     * @param {string} [filters.flag] - Triage bucket filter (rejected_denied, needs_review,
     *   zero_paid, crossover_stalled, stale, needs_secondary, crossover_pending, needs_attention)
     * @param {string} [filters.query] - Search patient name or payer name
     * @param {string} [filters.provider_id]
     * @param {string} [filters.service_date_from] - YYYY-MM-DD
     * @param {string} [filters.service_date_to] - YYYY-MM-DD
     * @param {string} [filters.start_date] - Filters created_at
     * @param {string} [filters.end_date] - Filters created_at
     * @param {Object} [filters.external] - Sort override, e.g. {"insurance_claim.created_at": "desc"}
     * @param {number} [filters.page]
     * @param {number} [filters.per_page]
     * Items carry `ledger` (CLAIM_LEDGER v1: visit paid by payer tier, secondary_state
     * adjudicated | open | none) once the ledger-derived endpoint is swapped in; null-safe reads.
     * @returns {Promise<import('@prism-ehr/shared').PaginatedResponse<import('@prism-ehr/shared').InsuranceClaimResponse>>}
     */
    getClaims(filters = {}) {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, val]) => {
        if (val === undefined || val === null) return;
        queryParams.append(key, typeof val === 'object' ? JSON.stringify(val) : val);
      });
      const qs = queryParams.toString();
      return this.request(`/billing/claims${qs ? `?${qs}` : ''}`, { method: 'GET' });
    },

    /**
     * Get a single insurance claim by ID.
     *
     * Xano: GET /billing/claims/:id
     *
     * Returns the claim with status_history, invoice, era_details (+ era_info),
     * secondary_children, payer_capabilities and, once the ledger-derived endpoint is
     * swapped in, `ledger` (CLAIM_LEDGER v1) with remit lines that posted to the visit
     * ledger under claim_id null marked `linked_via: 'ledger'`.
     *
     * @param {string} id
     * @returns {Promise<import('@prism-ehr/shared').InsuranceClaimResponse>}
     */
    getClaimDetail(id) {
      return this.request(`/billing/claims/${id}`, { method: 'GET' });
    },

    /**
     * Append an internal note to a claim. Author identity and timestamp are
     * stamped server-side from the auth token.
     *
     * Xano: POST /claim-notes
     *
     * @param {string} claimId
     * @param {string} note
     * @returns {Promise<import('@prism-ehr/shared').InsuranceClaimResponse>}
     */
    addClaimNote(claimId, note) {
      return this.request('/claim-notes', {
        method: 'POST',
        body: JSON.stringify({ claim_id: claimId, note })
      });
    },

    /**
     * Update an insurance claim. The server accepts a whitelist only:
     * correction_available, claim_data / billing_codes (unsent claims), the
     * draft to ready_to_submit status move, and reopening_requested (true marks
     * the claim as reopening requested, stamped with user and time server-side;
     * false clears that mark).
     *
     * Xano: PATCH /billing/claims/:id
     *
     * @param {string} id
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared').InsuranceClaimResponse>}
     */
    updateClaim(id, data) {
      return this.request(`/billing/claims/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    /**
     * Withdraw one or more claims -- marks them voided and clears
     * the encounter linkage so the encounter returns to the billing queue.
     * Also releases the invoice generated alongside the claim: an unpaid
     * pending_insurance invoice is deleted; an invoice with payments (or one
     * already in patient billing) is kept, unlinked from the claim, and
     * re-linked by the invoice generator on rebill. encounter.invoice_id is
     * cleared in both cases so the encounter re-enters Unbilled.
     *
     * Xano: POST /claim/withdraw
     * Xano staging file: 1348_claim_withdraw_POST.xs
     *
     * @param {string[]} claimIds
     * @returns {Promise<{success: boolean, withdrawn: Array<{id: string, encounter_id: string, encounter_cleared: boolean, invoice_deleted: boolean, invoice_unlinked: boolean, invoice_number: string, secondaries_voided: number}>, skipped: Object[]}>}
     */
    withdrawClaims(claimIds) {
      return this.request('/claim/withdraw', {
        method: 'POST',
        body: JSON.stringify({ claim_ids: claimIds })
      });
    },

    /**
     * Resend a rejected claim to the clearinghouse AS-IS: re-uploads the stored
     * payload under the same remote claim id (no regeneration, no new claim row).
     * For clearinghouse soft edits such as ERPRWK "re-submit claim to bypass".
     *
     * Xano: POST /claim/resend
     * Xano file: claim_resend_POST.xs
     *
     * @param {string} claimId
     * @returns {Promise<{success: boolean, claim_id: string, status: string, claimmd_id?: string, messages: Object[], rejection_messages: Object[]|null, error?: string}>}
     */
    resendClaim(claimId) {
      return this.request('/claim/resend', {
        method: 'POST',
        body: JSON.stringify({ claim_id: claimId })
      });
    },

    /**
     * Submit a batch of claims to the clearinghouse.
     *
     * Xano: POST /claim/submit-batch
     * Xano staging file: 966_claim_submit_batch_POST.xs
     *
     * @param {string[]} claimIds
     * @returns {Promise<{success: boolean, submitted_count: number, results: Object[], errors: Object[]}>}
     */
    submitClaimBatch(claimIds) {
      return this.request('/claim/submit-batch', {
        method: 'POST',
        body: JSON.stringify({ claim_ids: claimIds })
      });
    },

    /**
     * Poll claim statuses from ClaimMD.
     *
     * Xano: POST /claim/poll-status
     * Xano staging file: 972_claim_poll_status_POST.xs
     *
     * @returns {Promise<{success: boolean, updates_processed: number}>}
     */
    pollClaimStatus() {
      return this.request('/claim/poll-status', { method: 'POST' });
    },

    /**
     * Get RCM dashboard summary for claims: action counts, aging buckets, days in A/R.
     *
     * Xano: GET /billing/claims-dashboard
     *
     * @returns {Promise<{action_counts, aging_buckets, avg_days_in_ar, sla_days}>}
     */
    getClaimsDashboard() {
      return retryOnBusy(() => this.request('/billing/claims-dashboard', { method: 'GET', timeoutMs: 90000 }), {});
    },

    /**
     * Get invoice dashboard summary: action counts, aging buckets, avg days to collect.
     *
     * Xano: GET /billing/invoices-dashboard
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.provider_id] - Scope the totals to one provider's invoices
     * @returns {Promise<{action_counts, aging_buckets, avg_days_to_collect}>}
     */
    getInvoicesDashboard(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.provider_id) queryParams.append('provider_id', filters.provider_id);
      return this.request(`/billing/invoices-dashboard?${queryParams.toString()}`, { method: 'GET' });
    },

    /**
     * Update an invoice's collection status.
     * Values: 'in_dispute' | 'in_collections' | 'uncollectible' | null (clear)
     * When 'uncollectible', the invoice is also set to voided.
     *
     * Xano: PATCH /billing/invoices/:id/collection-status
     */
    updateInvoiceCollectionStatus(invoiceId, collectionStatus, reason) {
      return this.request(`/billing/invoices/collection-status/${invoiceId}`, {
        method: 'PATCH',
        body: JSON.stringify({ collection_status: collectionStatus, reason })
      });
    },

    /**
     * Record a manual reminder send — updates last_reminder_sent_at + reminder_count.
     * Does NOT send email/SMS — call sendPaymentLink for that.
     *
     * Xano: POST /billing/invoices/:id/reminder
     */
    recordInvoiceReminder(invoiceId) {
      return this.request(`/billing/invoices-reminder/${invoiceId}`, { method: 'POST' });
    },

    /**
     * Get the overview dashboard summary (KPIs, action queue, collections review).
     *
     * Xano: GET /billing/dashboard-summary
     */
    getBillingDashboardSummary() {
      return retryOnBusy(() => this.request('/billing/dashboard-summary', { method: 'GET', timeoutMs: 90000 }), {});
    },

    // ============ Remittances / ERA ============

    /**
     * Get remittances (ERA) list with optional filters.
     *
     * Xano: GET /billing/remittances
     *
     * @param {Object} [filters={}]
     * @returns {Promise<{items: import('@prism-ehr/shared').ERAResponse[]}>}
     */
    getRemittances(filters = {}) {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, val]) => {
        if (val !== undefined && val !== null) queryParams.append(key, val);
      });
      return this.request(`/billing/remittances?${queryParams.toString()}`, { method: 'GET' });
    },

    /**
     * Get a single remittance (ERA) by ID.
     *
     * Xano: GET /billing/remittances/:id
     *
     * @param {string} id
     * @returns {Promise<import('@prism-ehr/shared').ERAResponse>}
     */
    getRemittanceDetail(id) {
      return this.request(`/billing/remittances/${id}`, { method: 'GET' });
    },

    /**
     * Patch header fields on a remittance (ERA). Used for the manual bank
     * deposit date: the deposit-date cash basis reads the confirmed Bank Rec
     * match first and falls back to this value, then to payment_date.
     * The endpoint drops null/empty values, so a date can be replaced but
     * not cleared.
     *
     * Xano: PATCH /era/:era_id
     *
     * @param {string} eraId
     * @param {{deposit_date?: string}} data - YYYY-MM-DD
     * @returns {Promise<import('@prism-ehr/shared').ERAResponse>}
     */
    patchRemittance(eraId, data) {
      return this.request(`/era/${eraId}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    /**
     * Post a remittance (apply ERA payments to claims and invoices).
     * Matched claims are posted; unmatched details are marked as skipped.
     *
     * Xano: POST /billing/remittances/post/:era_id
     *
     * @param {string} eraId
     * @returns {Promise<{success: boolean, claims_posted: number, invoices_updated: number, skipped_count: number}>}
     */
    postRemittance(eraId) {
      return this.request(`/billing/remittances/post/${eraId}`, {
        method: 'POST'
      });
    },

    /**
     * Void a hand-keyed (manual) remittance so the check can be re-keyed.
     * Offsets every live ledger fact from its lines, restores or resets the
     * claims, re-projects the encounters, then deletes the lines and header.
     * dry_run (default true) reports what would happen and writes nothing.
     * Requires the Manage billing permission; clearinghouse remits and remits
     * matched to a bank deposit are refused.
     *
     * Xano: POST /billing/remittances-void
     *
     * @param {string} eraId
     * @param {{dry_run?: boolean, reason?: string}} [options]
     * @returns {Promise<{success: boolean, dry_run: boolean, detail_count: number, live_facts: number, live_total: number, claims_restored: number, claims_reset: number, claim_changes: Array, stamp_errors: Array}>}
     */
    voidRemittance(eraId, { dry_run = true, reason } = {}) {
      return this.request('/billing/remittances-void', {
        method: 'POST',
        body: JSON.stringify({ era_id: eraId, dry_run, ...(reason ? { reason } : {}) })
      });
    },

    /**
     * Manually match an ERA detail to an insurance claim.
     *
     * Xano: POST /era/manual-match
     * Xano staging file: 991_era_manual_match_POST.xs
     *
     * Response flags:
     * - `posted_as_secondary`: the line was applied to the visit as a second-payer
     *   payment; the primary claim's amounts were not changed.
     * - `financial_update_skipped`: the link was written but the amounts were not
     *   applied; `financial_skip_reason` says why (e.g. lower payment than the
     *   amount already stamped).
     * - `unmatched_remaining`: lines on the same ERA still without a claim.
     *
     * @param {string} eraDetailId
     * @param {string} claimId
     * @returns {Promise<{success: boolean, era_detail: Object, claim: Object, invoice_reconciled: boolean, posted_as_secondary: boolean, financial_update_skipped: boolean, financial_skip_reason: string|null, unmatched_remaining: number}>}
     */
    manualMatchERA(eraDetailId, claimId) {
      return this.request('/era/manual-match', {
        method: 'POST',
        body: JSON.stringify({ era_detail_id: eraDetailId, claim_id: claimId })
      });
    },

    /**
     * Create a manual ERA from a paper/PDF EOB (2026-09-14). The claims travel in
     * the SAME shape ClaimMD returns for an 835 (see utils/eob/toEraClaims.js) and
     * run the shared ingest (functions/fetch_era_details with `claims`): PCN
     * ladder, payer tiebreak, detail rows with pcn/payer_icn/charges, guards,
     * ledger facts for auto-matched lines, stamps. Call postRemittance() next to
     * settle netted reversals and post anything left matched-unposted.
     *
     * Xano: POST /billing/remittances/create-manual
     *
     * @param {Object} data
     * @param {string} data.check_or_eft_number - What the bank statement shows
     * @param {string} [data.payment_trace_number] - Payer trace / remittance id
     * @param {number} data.payment_amount - Check/trace amount as printed (never restated from the lines)
     * @param {string} data.payment_date - YYYY-MM-DD
     * @param {string} [data.payment_method]
     * @param {string} [data.payer_name]
     * @param {string} [data.payer_id] - ClaimMD payer id; drives the primary-payer tiebreak
     * @param {string} [data.file_data] - Source EOB as a data URI (stored as a private attachment)
     * @param {string} [data.file_name]
     * @param {Array<{pcn: string, payer_icn: string, status_code: '1'|'2'|'4'|'22', from_dos: string, pat_name_f?: string, pat_name_l?: string, total_charge: number, total_paid: number, patient_responsibility: number, adjustment: Array<{group: string, code: string, amount: number}>, charge: Array<{proc_code?: string, mod?: string, units?: number, from_dos: string, charge: number, allowed: number, paid?: number, adjustment: Array<{group: string, code: string, amount: number}>}>}>} data.claims
     * @returns {Promise<import('@prism-ehr/shared').CreateManualERAResponse>}
     */
    createManualERA(data) {
      return this.request('/billing/remittances/create-manual', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Poll ERA list from ClaimMD.
     *
     * Xano: POST /era/poll-list
     * Xano staging file: 973_era_poll_list_POST.xs
     *
     * @param {string} organizationId
     * @returns {Promise<{success: boolean, new_eras: number, era_ids: string[]}>}
     */
    pollERAList(organizationId) {
      return this.request('/era/poll-list', {
        method: 'POST',
        body: JSON.stringify({ organization_id: organizationId })
      });
    },

    /**
     * Fetch ERA details (triggers claim matching and invoice reconciliation).
     *
     * Xano: POST /era/fetch-details
     * Xano staging file: 979_era_fetch_details_POST.xs
     *
     * @param {string} eraId
     * @returns {Promise<{success: boolean, details_processed: number, auto_matched: number, needs_review: number, details: Object[]}>}
     */
    fetchERADetails(eraId) {
      return this.request('/era/fetch-details', {
        method: 'POST',
        body: JSON.stringify({ era_id: eraId })
      });
    },

    /**
     * List ERAs that could not be auto-assigned to an organization and that
     * the caller's org is eligible to claim (server-filtered). Admin only.
     *
     * Xano: GET /billing/remittances-unassigned
     *
     * @returns {Promise<{items: Array<{id: string, era_id: string, check_or_eft_number: string, payer_name: string, payer_id: string, payment_amount: number, payment_date: string, created_at: number, provider_npi: string, provider_tax_id: string, claim_count: number, reason: string, candidates: Array<{organization_id: string, via: string}>, pcn_match_count: number}>, count: number}>}
     */
    getUnassignedRemittances() {
      return this.request('/billing/remittances-unassigned', { method: 'GET' });
    },

    /**
     * Assign an unassigned ERA to the caller's organization, then fetch its
     * details (claim matching + invoice reconciliation). Admin only.
     *
     * Xano: POST /billing/remittances-assign-org
     *
     * @param {string} eraId
     * @returns {Promise<{success: boolean, organization_id: string, details_processed: number, auto_matched: number, needs_review: number}>}
     */
    assignRemittanceOrg(eraId) {
      return this.request('/billing/remittances-assign-org', {
        method: 'POST',
        body: JSON.stringify({ era_id: eraId })
      });
    },

    /**
     * Unattached remit money worklist: remit lines with money and no live
     * adjudication fact in the ledger ("received, not posted"). Each line is
     * bucketed as `unmatched`, `secondary_pending` (claim status code 2 with an
     * encounter; posts via the ERA), or `no_claim` (no live Prism claim for that
     * patient on that DOS). Admin only.
     *
     * Xano: GET /billing/remittances-unattached
     * Xano file: billing_remittances-unattached_GET.xs
     *
     * @param {Object} [params={}]
     * @param {string} [params.start] - YYYY-MM-DD on era.payment_date; default end minus 120 days
     * @param {string} [params.end] - YYYY-MM-DD; default today
     * @param {string} [params.status] - era_detail.match_status filter: unmatched | crossover_unmatched | skipped | manual_matched | auto_matched
     * @returns {Promise<{items: Array<{era_detail_id: string, era_id: string, era_source: string, payer_name: string, payment_date: string, check_or_eft_number: string, patient_name: string, patient_id: string, service_date: string, claim_status_code: string, crossover_carrier: string, total_paid: number, match_status: string, encounter_id: string, bucket: 'unmatched'|'secondary_pending'|'no_claim', has_prism_claim_candidate: boolean|null}>, count: number, total: number, by_match_status: Object<string, number>, by_bucket: Object<string, number>, window: {start: string, end: string}}>}
     */
    getUnattachedRemittances({ start, end, status, dos_from } = {}) {
      const queryParams = new URLSearchParams();
      if (start) queryParams.append('start', start);
      if (end) queryParams.append('end', end);
      if (status) queryParams.append('status', status);
      if (dos_from) queryParams.append('dos_from', dos_from);
      const qs = queryParams.toString();
      return this.request(`/billing/remittances-unattached${qs ? `?${qs}` : ''}`, { method: 'GET' });
    },

    // ============ Organization Payers ============

    /**
     * List org's in-network payers.
     *
     * Xano: GET /organization-payers
     *
     * @param {{ is_active?: boolean, search?: string, claimmd_payer_id?: string, display_name?: string }} [filters={}]
     * @returns {Promise<import('@prism-ehr/shared').OrganizationPayerResponse[]>}
     */
    getOrganizationPayers(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.is_active !== undefined) queryParams.append('is_active', filters.is_active);
      if (filters.search) queryParams.append('search', filters.search);
      if (filters.claimmd_payer_id) queryParams.append('claimmd_payer_id', filters.claimmd_payer_id);
      if (filters.display_name) queryParams.append('display_name', filters.display_name);
      return this.request(`/organization-payers?${queryParams.toString()}`, { method: 'GET' });
    },

    /**
     * Add a payer to org's in-network list.
     *
     * Xano: POST /organization-payers
     *
     * @param {{ claimmd_payer_id: string, display_name: string, payer_address?: string, payer_phone?: string, notes?: string }} data
     * @returns {Promise<import('@prism-ehr/shared').OrganizationPayerResponse>}
     */
    createOrganizationPayer(data) {
      return this.request('/organization-payers', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Update an org payer entry.
     *
     * Xano: PATCH /organization-payers/:id
     *
     * @param {string} id
     * @param {{ display_name?: string, payer_address?: string, payer_phone?: string, notes?: string, is_active?: boolean }} data
     * @returns {Promise<import('@prism-ehr/shared').OrganizationPayerResponse>}
     */
    updateOrganizationPayer(id, data) {
      return this.request(`/organization-payers/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    /**
     * Remove a payer from org's list.
     *
     * Xano: DELETE /organization-payers/:id
     *
     * @param {string} id
     * @returns {Promise<void>}
     */
    deleteOrganizationPayer(id) {
      return this.request(`/organization-payers/${id}`, { method: 'DELETE' });
    },

    /**
     * Patient insurance entries not yet linked to an in-network payer
     * (missing organization_payer_id). Feeds the Billing Settings cleanup view.
     *
     * Xano: GET /patient-insurance-unmatched
     *
     * @returns {Promise<{ total: number, items: Array<{ patient_id: string, patient_first_name: string, patient_last_name: string, entry_index: number, payer_name: string|null, claimmd_payer_id: string|null, member_id: string|null, is_primary: boolean, plan_type: string|null }> }>}
     */
    getUnmatchedInsurances() {
      return this.request('/patient-insurance-unmatched', { method: 'GET' });
    },

    // ============ Payer Groups ============

    /**
     * List org's payer groups (every organization_payer belongs to one group).
     *
     * Xano: GET /payer-groups
     *
     * @returns {Promise<import('@prism-ehr/shared').PayerGroupResponse[]>}
     */
    getPayerGroups() {
      return this.request('/payer-groups', { method: 'GET' });
    },

    /**
     * Create a payer group.
     *
     * Xano: POST /payer-groups
     *
     * @param {{ name: string, notes?: string }} data
     * @returns {Promise<import('@prism-ehr/shared').PayerGroupResponse>}
     */
    createPayerGroup(data) {
      return this.request('/payer-groups', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Update a payer group (rename, notes).
     *
     * Xano: PATCH /payer-groups/:id
     *
     * @param {string} id
     * @param {{ name?: string, notes?: string }} data
     * @returns {Promise<import('@prism-ehr/shared').PayerGroupResponse>}
     */
    updatePayerGroup(id, data) {
      return this.request(`/payer-groups/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    /**
     * Delete a payer group. Backend rejects when payers still reference it.
     *
     * Xano: DELETE /payer-groups/:id
     *
     * @param {string} id
     * @returns {Promise<void>}
     */
    deletePayerGroup(id) {
      return this.request(`/payer-groups/${id}`, { method: 'DELETE' });
    },

    /**
     * Backfill payer groups for the org (idempotent): ungrouped payers sharing a
     * claimmd_payer_id collapse into one group named from the payer directory.
     *
     * Xano: POST /payer-groups-seed
     *
     * @returns {Promise<import('@prism-ehr/shared').PayerGroupSeedResponse>}
     */
    seedPayerGroups() {
      return this.request('/payer-groups-seed', {
        method: 'POST',
        body: JSON.stringify({})
      });
    },

    // ============ Provider Payer Credentialing ============

    /**
     * List provider credentialing records. Absence of a record for a
     * (provider, payer group) pair means "no record".
     *
     * Xano: GET /provider-payer-credentialing
     *
     * @param {{ provider_id?: string, payer_group_id?: string, status?: string }} [filters={}]
     * @returns {Promise<import('@prism-ehr/shared').ProviderPayerCredentialingResponse[]>}
     */
    getProviderPayerCredentialing(filters = {}) {
      const queryParams = new URLSearchParams();
      if (filters.provider_id) queryParams.append('provider_id', filters.provider_id);
      if (filters.payer_group_id) queryParams.append('payer_group_id', filters.payer_group_id);
      if (filters.status) queryParams.append('status', filters.status);
      return this.request(`/provider-payer-credentialing?${queryParams.toString()}`, { method: 'GET' });
    },

    /**
     * Upsert a credentialing record (one per provider + payer group). The payload
     * is the full desired state — absent effective_date/notes clear those fields.
     *
     * Xano: POST /provider-payer-credentialing
     *
     * @param {{ provider_id: string, payer_group_id: string, status: import('@prism-ehr/shared').CredentialingStatus, effective_date?: number, notes?: string }} data
     * @returns {Promise<import('@prism-ehr/shared').ProviderPayerCredentialingResponse>}
     */
    upsertProviderPayerCredentialing(data) {
      return this.request('/provider-payer-credentialing', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Delete a credentialing record (returns the pair to "no record").
     *
     * Xano: DELETE /provider-payer-credentialing/:id
     *
     * @param {string} id
     * @returns {Promise<void>}
     */
    deleteProviderPayerCredentialing(id) {
      return this.request(`/provider-payer-credentialing/${id}`, { method: 'DELETE' });
    },

    // ============ Payer Search ============

    /**
     * Search for payers by name (proxies ClaimMD payer list).
     *
     * Xano: GET /payers/search
     *
     * @param {string} name
     * @returns {Promise<import('@prism-ehr/shared').PayerSearchResult[]>}
     */
    searchPayers(name) {
      return this.request(`/payers/search?name=${encodeURIComponent(name)}`, { method: 'GET' });
    },

    // ============ Fee Schedule ============

    /**
     * Get fee schedule entries with optional filters.
     *
     * Xano: GET /fee-schedule
     *
     * @param {Object} [filters={}]
     * @param {string} [filters.payer_id]
     * @param {string} [filters.cpt_code]
     * @returns {Promise<import('@prism-ehr/shared').FeeScheduleResponse[]>}
     */
    getFeeSchedule(filters = {}) {
      const queryParams = new URLSearchParams();
      Object.entries(filters).forEach(([key, val]) => {
        if (val !== undefined && val !== null) queryParams.append(key, val);
      });
      return this.request(`/fee-schedule?${queryParams.toString()}`, { method: 'GET' });
    },

    /**
     * Create a fee schedule entry.
     *
     * Xano: POST /fee-schedule
     *
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared').FeeScheduleResponse>}
     */
    createFeeScheduleEntry(data) {
      return this.request('/fee-schedule', {
        method: 'POST',
        body: JSON.stringify(data)
      });
    },

    /**
     * Update a fee schedule entry.
     *
     * Xano: PATCH /fee-schedule/:id
     *
     * @param {string} id
     * @param {Object} data
     * @returns {Promise<import('@prism-ehr/shared').FeeScheduleResponse>}
     */
    updateFeeScheduleEntry(id, data) {
      return this.request(`/fee-schedule/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(data)
      });
    },

    /**
     * Delete a fee schedule entry.
     *
     * Xano: DELETE /fee-schedule/:id
     *
     * @param {string} id
     * @returns {Promise<void>}
     */
    deleteFeeScheduleEntry(id) {
      return this.request(`/fee-schedule/${id}`, { method: 'DELETE' });
    },

    /**
     * Bulk import fee schedule entries from CSV data.
     *
     * Xano: POST /fee-schedule/import
     *
     * @param {Object[]} entries
     * @returns {Promise<{success: boolean, imported: number, errors: Object[]}>}
     */
    importFeeSchedule(entries) {
      return this.request('/fee-schedule/import', {
        method: 'POST',
        body: JSON.stringify({ entries })
      });
    },

    // ============ Fee Lookup ============

    /**
     * Look up the best available rate for a CPT code.
     * Cascade: fee_schedule → Medicare rate × multiplier → null.
     *
     * Xano: GET /fee-lookup
     *
     * @param {string} cptCode - CPT/HCPCS code
     * @param {string} [patientId] - Patient ID (enables payer-specific fee schedule lookup)
     * @param {string} [modifier] - Modifier code (e.g., '26', 'TC')
     * @param {number|string} [caseId] - Case ID; lets the backend honor the case's billing_coverage when picking the payer fee schedule
     * @returns {Promise<import('@prism-ehr/shared').FeeLookupResponse>}
     */
    feeLookup(cptCode, patientId = null, modifier = '', caseId = null) {
      const params = new URLSearchParams({ cpt_code: cptCode });
      if (patientId) params.append('patient_id', patientId);
      if (modifier) params.append('modifier', modifier);
      if (caseId) params.append('case_id', caseId);
      return this.request(`/fee-lookup?${params.toString()}`, { method: 'GET' });
    },

    /**
     * Resolve live rates for a batch of CPT codes in one request.
     * Same cascade as feeLookup (catalog_rate → fee_schedule → Medicare × multiplier → null)
     * with a fixed number of queries regardless of code count. Every requested code
     * is present in the response (`rate: null, source: null` when nothing resolved).
     *
     * Xano: POST /fee-lookup-batch
     *
     * @param {string[]} cptCodes - CPT/HCPCS codes (deduped server-side, max 50)
     * @param {string} [patientId] - Patient ID (enables payer-specific fee schedule lookup)
     * @param {number|string} [caseId] - Case ID; honors the case's billing_coverage when picking the payer fee schedule
     * @returns {Promise<import('@prism-ehr/shared').FeeLookupBatchResponse>}
     */
    feeLookupBatch(cptCodes, patientId = null, caseId = null) {
      const body = { cpt_codes: cptCodes };
      if (patientId) body.patient_id = patientId;
      if (caseId) body.case_id = caseId;
      return this.request('/fee-lookup-batch', { method: 'POST', body: JSON.stringify(body) });
    },

    // ============ Medicare Data Import ============

    /**
     * Bulk import Medicare RVU data.
     *
     * Xano: POST /admin/medicare-rvu/import
     *
     * @param {Object[]} entries - Array of RVU records
     * @returns {Promise<{imported: number, errors: Object[]}>}
     */
    importMedicareRVU(entries) {
      return this.request('/admin/medicare-rvu/import', {
        method: 'POST',
        body: JSON.stringify({ entries })
      });
    },

    /**
     * Bulk import Medicare GPCI data.
     *
     * Xano: POST /admin/medicare-gpci/import
     *
     * @param {Object[]} entries - Array of GPCI records
     * @returns {Promise<{imported: number, errors: Object[]}>}
     */
    importMedicareGPCI(entries) {
      return this.request('/admin/medicare-gpci/import', {
        method: 'POST',
        body: JSON.stringify({ entries })
      });
    },

    /**
     * Bulk import Medicare locality crosswalk data.
     *
     * Xano: POST /admin/medicare-locality/import
     *
     * @param {Object[]} entries - Array of locality crosswalk records
     * @returns {Promise<{imported: number, errors: Object[]}>}
     */
    importMedicareLocality(entries) {
      return this.request('/admin/medicare-locality/import', {
        method: 'POST',
        body: JSON.stringify({ entries })
      });
    },

    // ============ Medicare Therapy Cap ============

    /**
     * Get Medicare therapy cap status for a patient.
     * Returns cumulative therapy charges for current calendar year vs $2,480 threshold.
     *
     * Xano: GET /billing-therapy-cap-status
     *
     * @param {string} patientId
     * @returns {Promise<import('@prism-ehr/shared').TherapyCapStatus>}
     */
    getTherapyCapStatus(patientId) {
      return this.request(`/billing-therapy-cap-status?patient_id=${patientId}`, { method: 'GET' });
    },

    /**
     * Real-time Medicare therapy cap check via ClaimMD.
     * Makes a single eligibility call (service_code 30) to get fresh therapy spending.
     * Updates patient.insurance with the fresh data.
     *
     * Xano: POST /eligibility/therapy-cap-check
     *
     * @param {string} patientId
     * @param {string} organizationTaxId
     * @returns {Promise<import('@prism-ehr/shared').TherapyCapStatus>}
     */
    checkTherapyCapRealtime(patientId, organizationTaxId) {
      return this.request('/eligibility/therapy-cap-check', {
        method: 'POST',
        body: JSON.stringify({ patient_id: patientId, organization_tax_id: organizationTaxId })
      });
    },

    // ============ Claim Attachments ============

    /**
     * Submit an attachment to a claim.
     *
     * Xano: POST /claim/:id/attach
     *
     * @param {string} claimId
     * @param {File} file
     * @param {string} attachmentType
     * @returns {Promise<import('@prism-ehr/shared').ClaimAttachmentResponse>}
     */
    submitClaimAttachment(claimId, file, attachmentType) {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('attachment_type', attachmentType);
      return this.request(`/claim/${claimId}/attach`, {
        method: 'POST',
        timeoutMs: 120000,
        body: formData
      });
    },

    // ============ Patient Balance ============

    /**
     * Get outstanding patient balance (from Veradigm PM or Prism invoices).
     * Prism responses also include charges from completed encounters that have
     * no invoice and no insurance claim (e.g., PI/lien cases).
     *
     * Xano: GET /patient-billing-balance
     *
     * @param {string} patientId
     * @returns {Promise<{patient_balance: number, total_fees: number, total_payments: number, total_adjustments: number, uninvoiced_charges: number, uninvoiced_encounter_count: number, total_balance: number, source: string}>}
     */
    getPatientBillingBalance(patientId) {
      return this.request(`/patient-billing-balance?patient_id=${patientId}`, {
        method: 'GET'
      });
    },

    // ============ Patient Payment Methods ============

    /**
     * Get saved payment methods for a patient.
     *
     * Xano: GET /patients/{patient_id}/payment-methods
     * Xano staging file: 874_patients_patient_id_payment_methods_GET.xs
     *
     * @param {string} patientId
     * @returns {Promise<{payment_methods: Array, default_payment_method_id: string|null, stripe_customer_id: string}>}
     */
    getPatientPaymentMethods(patientId) {
      return this.request(`/patients/${patientId}/payment-methods`, {
        method: 'GET'
      });
    },

    /**
     * Create a Stripe PaymentIntent for collecting a payment.
     *
     * Xano: POST /billing/create-payment-intent
     * Xano staging file: 847_billing_create_payment_intent_POST.xs
     *
     * @param {Object} params
     * @param {string} params.invoice_id
     * @param {string} params.patient_id
     * @param {number} params.amount
     * @param {string} params.description
     * @param {boolean} [params.save_payment_method]
     * @param {string} [params.payment_method_id]
     * @returns {Promise<{client_secret: string, payment_intent_id: string, customer_id: string}>}
     */
    createPaymentIntent({ invoice_id, patient_id, amount, description, save_payment_method, payment_method_id }) {
      return this.request('/billing/create-payment-intent', {
        method: 'POST',
        body: JSON.stringify({ invoice_id, patient_id, amount, description, save_payment_method, payment_method_id })
      });
    },

    /**
     * Create a Stripe SetupIntent for saving a payment method to a patient.
     *
     * Xano: POST /billing/create-setup-intent
     * Xano staging file: 848_billing_create_setup_intent_POST.xs
     *
     * @param {string} patientId
     * @returns {Promise<{client_secret: string, customer_id: string}>}
     */
    createSetupIntent(patientId) {
      return this.request('/billing/create-setup-intent', {
        method: 'POST',
        body: JSON.stringify({ patient_id: patientId })
      });
    },

    /**
     * Set the default payment method for a patient.
     *
     * Xano: POST /billing/patient-payment-methods-set-default
     * Xano staging file: billing_patient-payment-methods-set-default_POST.xs
     *
     * @param {string} patientId
     * @param {string} paymentMethodId - Stripe payment method ID
     * @returns {Promise<{success: boolean, default_payment_method_id: string}>}
     */
    setPatientDefaultPaymentMethod(patientId, paymentMethodId) {
      return this.request('/billing/patient-payment-methods-set-default', {
        method: 'POST',
        body: JSON.stringify({ patient_id: patientId, payment_method_id: paymentMethodId })
      });
    },

    /**
     * Delete/detach a payment method from a patient.
     *
     * Xano: DELETE /billing/patient-payment-methods
     * Xano staging file: billing_patient-payment-methods_DELETE.xs
     *
     * @param {string} patientId
     * @param {string} paymentMethodId - Stripe payment method ID
     * @returns {Promise<{success: boolean, message: string}>}
     */
    deletePatientPaymentMethod(patientId, paymentMethodId) {
      return this.request(`/billing/patient-payment-methods?patient_id=${patientId}&payment_method_id=${paymentMethodId}`, {
        method: 'DELETE'
      });
    },

    // ============ Organization Branding ============

    /**
     * Upload an organization logo. Stores in Xano file storage
     * and saves URL to organization.settings.branding.logo_url.
     *
     * Xano: POST /organization/upload-logo
     *
     * @param {File} file - Image file (PNG, JPG, SVG)
     * @returns {Promise<Object>} Updated organization record
     */
    async uploadOrganizationLogo(file) {
      const fileToBase64 = (f) => new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.readAsDataURL(f);
        reader.onload = () => resolve(reader.result);
        reader.onerror = reject;
      });
      const base64Data = await fileToBase64(file);
      return this.request('/organization/upload-logo', {
        method: 'POST',
        body: JSON.stringify({
          logo_file_data: base64Data,
          logo_file_name: file.name
        })
      });
    },

    // ============ Public Billing (Unauthenticated) ============

    /**
     * Get Stripe billing config for an organization (public/unauthenticated).
     *
     * Xano: POST /public/billing-config
     *
     * @param {string} organizationId
     * @returns {Promise<{stripe_publishable_key: string, stripe_account_id: string}>}
     */
    async getPublicBillingConfig(organizationId) {
      return this.publicRequest('/public/billing-config', {
        method: 'POST',
        body: JSON.stringify({ organization_id: organizationId })
      });
    },

    /**
     * Create a Stripe SetupIntent for a patient (public/unauthenticated).
     * Used for collecting payment methods on public forms.
     *
     * Xano: POST /public/create-setup-intent
     *
     * @param {string} patientId
     * @param {string} organizationId
     * @returns {Promise<{client_secret: string, customer_id: string}>}
     */
    async createPublicSetupIntent(patientId, organizationId) {
      return this.publicRequest('/public/create-setup-intent', {
        method: 'POST',
        body: JSON.stringify({ patient_id: patientId, organization_id: organizationId })
      });
    },

    // ============ Encounter Holds ============

    /**
     * List held encounters for the organization.
     *
     * Xano: GET /encounter-holds
     * Xano file: encounter-holds_GET.xs
     *
     * @param {Object} [filters]
     * @param {boolean} [filters.is_active]
     * @param {'general'|'prior_auth'|'removed'|'addendum_review'|'all'} [filters.hold_type] - 'general' includes legacy untyped holds; 'addendum_review' = posted Veradigm visits returned to billing after an addendum; defaults to 'all'
     * @param {number} [filters.page]
     * @param {number} [filters.limit]
     * @returns {Promise<Object>} Paginated list of EncounterHold records with joined encounter/patient/provider
     */
    getEncounterHolds(filters = {}) {
      const params = new URLSearchParams();
      Object.entries(filters).forEach(([key, value]) => {
        if (value !== undefined && value !== null && value !== '') {
          params.append(key, value);
        }
      });
      const query = params.toString();
      return this.request(`/encounter-holds${query ? `?${query}` : ''}`, { method: 'GET' });
    },

    /**
     * Place a hold on an encounter.
     *
     * Xano: POST /encounter-holds
     * Xano file: encounter-holds_POST.xs
     *
     * @param {{ encounter_id: string, held_reason?: string, hold_type?: 'general'|'prior_auth'|'removed'|'addendum_review' }} data - held_reason required for all types except prior_auth; addendum_review does not set encounter.is_held
     * @returns {Promise<Object>} Created EncounterHold record
     */
    createEncounterHold(data) {
      return this.request('/encounter-holds', {
        method: 'POST',
        body: JSON.stringify(data),
      });
    },

    /**
     * Release a hold on an encounter. With options.clinical_review_note, the backend
     * additionally flags the encounter for clinical review in the same request
     * (and clears any stale clinical_review_resolved_at so it re-enters the queue).
     *
     * Xano: PATCH /encounter-holds/{id}
     * Xano file: encounter-holds_encounter_hold_id_PATCH.xs
     *
     * @param {string} holdId
     * @param {{ clinical_review_note?: string }} [options] - note pre-built via buildReviewNote (label-prefix format is load-bearing for backend auto-resolve)
     * @returns {Promise<Object>} Updated hold record
     */
    releaseEncounterHold(holdId, options = {}) {
      const hasBody = options.clinical_review_note != null && options.clinical_review_note !== '';
      return this.request(`/encounter-holds/${holdId}`, {
        method: 'PATCH',
        ...(hasBody ? { body: JSON.stringify({ clinical_review_note: options.clinical_review_note }) } : {}),
      });
    },

    /**
     * Derived episode state for one encounter (billing ledger dual-run).
     *
     * Xano: GET /billing/episode-state
     * Xano file: billing_episode-state_GET.xs
     *
     * @param {string} encounterId
     * @returns {Promise<Object>} { billed, allowed, payers, patient, balance, responsible_party, live_claims, flags, drift }
     */
    getEpisodeState(encounterId) {
      const queryParams = new URLSearchParams();
      queryParams.append('encounter_id', encounterId);
      return this.request(`/billing/episode-state?${queryParams.toString()}`, { method: 'GET' });
    },

    /**
     * Run the derivation audit: buckets ledger-vs-legacy agreement over the
     * org's recent claimed encounters. Read-only scorer for the migration.
     *
     * Xano: POST /billing/derivation-audit
     * Xano file: billing_derivation-audit_POST.xs
     *
     * @param {{ limit?: number, offset?: number, days_back?: number, include_veradigm?: boolean }} [options]
     * @returns {Promise<Object>} { match, drift, no_events, drift_samples, cross_encounter_duplicate_sources, next_offset }
     */
    runDerivationAudit(options = {}) {
      return this.request('/billing/derivation-audit', {
        method: 'POST',
        body: JSON.stringify(options),
      });
    },

    /**
     * Migration Step 2: backfill historical money facts into the ledger.
     * Dry-run-first operator tool (no UI).
     *
     * Xano: POST /billing/ledger-backfill
     * Xano file: billing_ledger-backfill_POST.xs
     *
     * @param {{ dry_run?: boolean, limit?: number, offset?: number, include_veradigm?: boolean, backfill_before: string }} options - backfill_before = Step 1 ship time (ISO)
     * @returns {Promise<Object>} counts + unapplied_paying_details + residual_samples + next_offset
     */
    runLedgerBackfill(options = {}) {
      return this.request('/billing/ledger-backfill', {
        method: 'POST',
        body: JSON.stringify(options),
      });
    },

    /**
     * Migration Step 2: repair duplicate live claim chains (invariant I1).
     * Dry-run-first operator tool (no UI).
     *
     * Xano: POST /billing/repair-chain-integrity
     * Xano file: billing_repair-chain-integrity_POST.xs
     *
     * @param {{ dry_run?: boolean, limit?: number, offset?: number }} [options]
     * @returns {Promise<Object>} counts + void_candidates + regenerate_secondary_candidates + repair_log
     */
    runChainRepair(options = {}) {
      return this.request('/billing/repair-chain-integrity', {
        method: 'POST',
        body: JSON.stringify(options),
      });
    },

    /**
     * Step 1.5 (invoice slice): stamp derived episode balances onto invoices
     * frozen in pending_insurance. HELD until customer billing policy is
     * decided — dry-run sizes the dollars; min_service_date implements a
     * cutoff policy. Operator tool, no UI.
     *
     * Xano: POST /billing/invoice-balance-stamp
     * Xano file: billing_invoice-balance-stamp_POST.xs
     *
     * @param {{ dry_run?: boolean, limit?: number, offset?: number, min_service_date?: string }} [options]
     * @returns {Promise<Object>} { stamped_sent, stamped_paid, stamped_dollars, skipped_*, cutoff_samples, next_offset }
     */
    runInvoiceBalanceStamp(options = {}) {
      return this.request('/billing/invoice-balance-stamp', {
        method: 'POST',
        body: JSON.stringify(options),
      });
    },
  };
}
